import { useMemo, useState } from "react";

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

// YYYY-MM-DD in local time
const toIsoDay = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Build the 6-row Monday-first grid of Date objects that covers the given
// month. Days outside the month are included so the grid is always full.
const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  // weekday index with Monday = 0
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

// Availability calendar shown inside the time-off drawer.
// - `startDate`/`endDate` (ISO date strings): the requested time-off window
// - `assignedJobDates`: Set of "YYYY-MM-DD" strings where the user has a job
function AvailabilityPreview({ startDate, endDate, assignedJobDates }) {
  // initial month: the requested start, or today
  const initial = startDate ? new Date(startDate) : new Date();
  const [cursor, setCursor] = useState(
    new Date(initial.getFullYear(), initial.getMonth(), 1),
  );

  const days = useMemo(
    () => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor],
  );

  const reqStart = startDate ? new Date(startDate) : null;
  const reqEnd = endDate ? new Date(endDate) : reqStart;
  const inRequest = (d) =>
    reqStart && reqEnd && d >= stripTime(reqStart) && d <= stripTime(reqEnd);

  const monthLabel = cursor.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const goPrev = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));

  return (
    <div className="flex flex-col gap-3">
      <span className="text-[11px] font-InterSemiBold text-[#6B7280] uppercase leading-[16.5px]">
        Availability Preview
      </span>

      <div className="self-stretch flex flex-col items-start gap-4 p-4 rounded-xl border border-[#E5E7EB] bg-white">
        {/* month header */}
        <div className="self-stretch flex items-center justify-between">
          <button
            onClick={goPrev}
            className="w-7 h-7 flex items-center justify-center text-[20px] text-[#6B7280] hover:text-[#1E293B]"
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="text-[16px] font-InterBold text-[#1E293B] leading-6">
            {monthLabel}
          </span>
          <button
            onClick={goNext}
            className="w-7 h-7 flex items-center justify-center text-[20px] text-[#6B7280] hover:text-[#1E293B]"
            aria-label="Next month"
          >
            ›
          </button>
        </div>

        {/* weekday header + days grid */}
        <div className="self-stretch grid grid-cols-7 gap-y-1">
          {WEEKDAY_LABELS.map((d, i) => (
            <div
              key={i}
              className="text-center text-[12px] font-InterMedium text-[#9CA3AF] leading-4"
            >
              {d}
            </div>
          ))}

          {days.map((d) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            const requested = inRequest(d);
            const hasJob =
              assignedJobDates && assignedJobDates.has(toIsoDay(d));

            return (
              <div
                key={d.toISOString()}
                className="relative h-7 px-1 flex items-center justify-center"
              >
                <div
                  className={`relative w-full h-full flex items-center justify-center text-[14px] leading-5 ${
                    requested
                      ? "rounded-md border border-dashed border-[#A855F7] bg-[#FAF5FF] font-InterBold text-[#7E22CE]"
                      : !inMonth
                        ? "text-[#D1D5DB] font-InterRegular"
                        : isWeekend
                          ? "rounded-md bg-[#F3F4F6] text-[#9CA3AF] font-InterRegular"
                          : "text-[#1E293B] font-InterRegular"
                  }`}
                >
                  {d.getDate()}
                  {hasJob && (
                    <span className="absolute left-1/2 top-1/2 mt-2.5 -translate-x-1/2 w-1 h-1 rounded-full bg-[#F59E0B]" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* legend */}
        <div className="self-stretch flex items-center justify-center gap-4 pt-2 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded border border-dashed border-[#D8B4FE] bg-[#F3E8FF]" />
            <span className="text-[12px] font-InterRegular text-[#6B7280] leading-4">
              Requested
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
            <span className="text-[12px] font-InterRegular text-[#6B7280] leading-4">
              Assigned Job
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// strip the time so date-only comparisons work
const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export default AvailabilityPreview;
